import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Paper,
  Grid,
  Button,
  CircularProgress,
} from "@mui/material";
import { BASE_URL } from "../../constants";
import { toast } from "react-toastify";

const CentreManagement = ({ allCenters }) => {
  const [centers, setCenters] = useState([]); // Local copy of all centers
  const [searchQuery, setSearchQuery] = useState("");
  const [passwords, setPasswords] = useState({});
  const [loading, setLoading] = useState({});

  useEffect(() => {
    setCenters(allCenters);
  }, [allCenters]);

  const filteredCenters = centers.filter((center) =>
    Object.values(center).some(
      (value) =>
        value &&
        value.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const handlePasswordChange = (centreId, value) => {
    setPasswords((prev) => ({
      ...prev,
      [centreId]: value,
    }));
  };

  const handleSetPassword = async (centreId) => {
    const password = passwords[centreId] || "";
    if (!password.trim()) {
      alert("Please enter a password before setting.");
      return;
    }

    try {
      setLoading((prev) => ({ ...prev, [centreId]: true }));
      const res = await fetch(`${BASE_URL}api/org/set-center-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ centerId: centreId, password }),
      });

      if (!res.ok) {
        throw new Error("Failed to set password");
      }

      const data = await res.json();

      if (data.success) {
        // Update password in local state immediately
        setCenters((prev) =>
          prev.map((center) =>
            center._id === centreId
              ? { ...center, centrePassword: password }
              : center
          )
        );

        toast.success("Password set successfully!");
        setPasswords((prev) => ({ ...prev, [centreId]: "" }));
      }
    } catch (err) {
      console.error(err);
      toast.error("Error setting password");
    } finally {
      setLoading((prev) => ({ ...prev, [centreId]: false }));
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h4" mb={2} fontWeight="bold">
        Centre Management
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          label="Search Centers"
          variant="outlined"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </Paper>

      <Paper sx={{ p: 2, overflowX: "auto" }}>
        <Grid
          container
          sx={{
            fontWeight: "bold",
            backgroundColor: "#1976d2",
            color: "#fff",
            p: 1,
          }}
        >
          <Grid item xs={2}>
            Centre ID
          </Grid>
          <Grid item xs={2}>
            Name
          </Grid>
          <Grid item xs={2}>
            Address
          </Grid>
          <Grid item xs={2}>
            Contact Person
          </Grid>
          <Grid item xs={2}>
            Contact No.
          </Grid>
          <Grid item xs={2}>
            Assign Password
          </Grid>
        </Grid>

        {filteredCenters.map((center, index) => (
          <Grid
            container
            key={center._id || index}
            sx={{
              p: 1,
              borderBottom: "1px solid #ddd",
              "&:hover": { backgroundColor: "#f5f5f5" },
              alignItems: "center",
            }}
          >
            <Grid item xs={2}>
              {center.centreId}
            </Grid>
            <Grid item xs={2}>
              {center.centreName}
            </Grid>
            <Grid item xs={2}>
              {center.centreAddress}
            </Grid>
            <Grid item xs={2}>
              {center.centreContactPerson}
            </Grid>
            <Grid item xs={2}>
              {center.centreContactNumber}
            </Grid>
            <Grid item xs={2} sx={{ display: "flex", gap: 1 }}>
              {center.centrePassword ? (
                <span>{center.centrePassword}</span>
              ) : (
                <>
                  <TextField
                    size="small"
                    type="text"
                    placeholder="Enter password"
                    value={passwords[center._id] || ""}
                    onChange={(e) =>
                      handlePasswordChange(center._id, e.target.value)
                    }
                  />
                  <Button
                    variant="contained"
                    size="small"
                    color="success"
                    disabled={loading[center._id]}
                    onClick={() => handleSetPassword(center._id)}
                  >
                    {loading[center._id] ? (
                      <CircularProgress size={18} sx={{ color: "white" }} />
                    ) : (
                      "Set"
                    )}
                  </Button>
                </>
              )}
            </Grid>
          </Grid>
        ))}
      </Paper>
    </Box>
  );
};

export default CentreManagement;
